import { zodResolver } from '@hookform/resolvers/zod';
import { ErrorCode, loginSchema, type LoginInput } from '@qualicloud/contracts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  Field,
  FormError,
  IconAlertOctagon,
  IconClock,
  IconInfo,
  IconLock,
  IconShieldCheck,
  Input,
  PasswordInput,
  IconQualiCloud,
} from '@/design-system';
import { ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/format/datetime';
import { useAuth } from '@/providers/auth-provider';
import { navigateTarget } from '@/routes/link-target';

/**
 * Tela de entrada.
 *
 * Trata explicitamente os quatro desfechos que o IDENTITY distingue e que o
 * legado embaralhava:
 *   AUTH.INVALID_CREDENTIALS — usuário ou senha errados
 *   AUTH.ACCOUNT_LOCKED      — lockout progressivo §11.300(d), com o horário de
 *                              destravamento
 *   AUTH.PASSWORD_EXPIRED    — leva à troca obrigatória de senha
 *   AUTH.MFA_REQUIRED        — revela o campo de segundo fator
 *
 * DECISÃO DE SEGURANÇA: a mensagem de credencial inválida é IDÊNTICA para
 * usuário inexistente e senha errada, e nunca informa "restam N tentativas".
 * O legado dizia quantas tentativas restavam (BUG-07,
 * `controller/common/login.php:141-144`), o que permitia enumerar usuários — a
 * diferença de resposta já era o oráculo.
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as unknown as { redirect?: string };

  const [needsMfa, setNeedsMfa] = useState(false);
  const [failure, setFailure] = useState<LoginFailure | null>(null);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '', organizationSlug: '', totp: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFailure(null);
    try {
      const user = await login({
        username: values.username.trim(),
        password: values.password,
        ...(values.organizationSlug?.trim()
          ? { organizationSlug: values.organizationSlug.trim() }
          : {}),
        ...(values.totp ? { totp: values.totp } : {}),
      });

      if (user.mustChangePassword) {
        void navigate({ to: '/trocar-senha' });
        return;
      }
      // O destino vem da query string; por isso passa pelo conversor controlado.
      void navigate(navigateTarget(search.redirect ?? '/'));
    } catch (error) {
      const parsed = classifyLoginFailure(error);
      if (parsed.code === ErrorCode.MFA_REQUIRED) setNeedsMfa(true);
      if (parsed.code === ErrorCode.PASSWORD_EXPIRED) {
        // A senha expirou mas a credencial está correta: o IDENTITY não abre
        // sessão. Levamos o usuário à troca com o contexto preservado.
        void navigate({
          to: '/trocar-senha',
          search: { expired: true, username: values.username.trim() },
        });
        return;
      }
      setFailure(parsed);
      form.setValue('password', '');
    }
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          {/* Assinatura da marca: o símbolo da QualiCloud e, abaixo, o nome do
              produto. O "eQMS" fica em peso normal porque é o PRODUTO — a marca
              é "QualiCloud", e a hierarquia visual precisa dizer isso. */}
          <IconQualiCloud size={52} className="text-brand" />
          <h1 className="text-2xl font-semibold text-content">
            QualiCloud <span className="font-normal text-content-muted">eQMS</span>
          </h1>
          <p className="text-sm text-content-muted">
            Sistema de Gestão da Qualidade e Gestão Eletrônica de Documentos
          </p>
        </div>

        <Card padding="lg">
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <h2 className="text-lg font-semibold text-content">Entrar</h2>

            {failure ? <LoginFailureBanner failure={failure} /> : null}

            <Field
              label="Usuário"
              required
              error={form.formState.errors.username?.message}
            >
              <Input
                {...form.register('username')}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
              />
            </Field>

            <Field label="Senha" required error={form.formState.errors.password?.message}>
              <PasswordInput {...form.register('password')} autoComplete="current-password" />
            </Field>

            <Field
              label="Organização"
              error={form.formState.errors.organizationSlug?.message}
              description="Preencha apenas se o seu usuário existir em mais de uma organização."
            >
              <Input
                {...form.register('organizationSlug')}
                autoComplete="organization"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="minha-empresa"
              />
            </Field>

            {needsMfa ? (
              <Field
                label="Código de verificação"
                required
                error={form.formState.errors.totp?.message}
                description="Código de 6 dígitos do seu aplicativo autenticador."
              >
                <Input
                  {...form.register('totp')}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  className="w-36 text-center font-mono text-lg tracking-[0.4em]"
                />
              </Field>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-1 w-full"
              loading={form.formState.isSubmitting}
              loadingLabel="Verificando suas credenciais…"
            >
              Entrar
            </Button>
          </form>
        </Card>

        <p className="mt-4 flex items-start gap-2 rounded-md border border-border bg-surface px-3 py-2.5 text-xs text-content-muted">
          <IconShieldCheck size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-brand" />
          <span>
            Ambiente validado conforme 21 CFR Part 11, ISO 13485 e ANVISA RDC 665. Todas as ações
            são registradas em trilha de auditoria imutável, incluindo tentativas de entrada
            malsucedidas.
          </span>
        </p>
      </div>
    </main>
  );
}

interface LoginFailure {
  readonly code: string;
  readonly title: string;
  readonly message: string;
  readonly tone: 'danger' | 'warning';
  readonly lockedUntil?: string;
}

function classifyLoginFailure(error: unknown): LoginFailure {
  if (!(error instanceof ApiError)) {
    return {
      code: 'UNKNOWN',
      title: 'Não foi possível entrar',
      message: 'Ocorreu um erro inesperado. Tente novamente em alguns instantes.',
      tone: 'danger',
    };
  }

  switch (error.code) {
    case ErrorCode.INVALID_CREDENTIALS:
      return {
        code: error.code,
        title: 'Usuário ou senha incorretos',
        // Nunca dizemos qual dos dois está errado, nem quantas tentativas
        // restam: ambos seriam oráculo de enumeração de usuários.
        message:
          'Verifique os dados e tente novamente. Tentativas malsucedidas sucessivas bloqueiam a conta temporariamente.',
        tone: 'danger',
      };

    case ErrorCode.ACCOUNT_LOCKED: {
      const lockedUntil = extractLockedUntil(error);
      return {
        code: error.code,
        title: 'Conta temporariamente bloqueada',
        message: lockedUntil
          ? `A conta foi bloqueada por excesso de tentativas malsucedidas e será liberada ${formatRelative(
              lockedUntil,
            )}. Se precisar de acesso imediato, solicite o desbloqueio ao administrador da sua organização.`
          : 'A conta foi bloqueada por excesso de tentativas malsucedidas. Aguarde o fim do bloqueio ou solicite o desbloqueio ao administrador da sua organização.',
        tone: 'warning',
        ...(lockedUntil ? { lockedUntil } : {}),
      };
    }

    case ErrorCode.ACCOUNT_INACTIVE:
      return {
        code: error.code,
        title: 'Conta inativa',
        message:
          'Esta conta está desativada e não pode acessar o sistema. Procure o administrador da sua organização.',
        tone: 'warning',
      };

    case ErrorCode.MFA_REQUIRED:
      return {
        code: error.code,
        title: 'Segundo fator obrigatório',
        message: 'Informe o código de 6 dígitos do seu aplicativo autenticador para continuar.',
        tone: 'warning',
      };

    case ErrorCode.MFA_INVALID:
      return {
        code: error.code,
        title: 'Código de verificação inválido',
        message:
          'O código informado é inválido ou já expirou. Aguarde o próximo código no aplicativo e tente novamente.',
        tone: 'danger',
      };

    case ErrorCode.RATE_LIMITED:
      return {
        code: error.code,
        title: 'Muitas tentativas',
        message:
          'Foram feitas muitas tentativas a partir deste endereço. Aguarde alguns minutos antes de tentar novamente.',
        tone: 'warning',
      };

    case ErrorCode.PASSWORD_EXPIRED:
      return {
        code: error.code,
        title: 'Senha expirada',
        message: 'Sua senha expirou. Defina uma nova senha para continuar.',
        tone: 'warning',
      };

    default:
      return {
        code: error.code,
        title: 'Não foi possível entrar',
        message: error.message,
        tone: 'danger',
      };
  }
}

/**
 * O IDENTITY informa o horário de destravamento no corpo do erro
 * (`fieldErrors.lockedUntil`). Exibir "será liberada em 12 minutos" evita que o
 * usuário fique tentando e reinicie o ciclo de backoff.
 */
function extractLockedUntil(error: ApiError): string | undefined {
  const candidate = error.fieldErrors.lockedUntil?.[0];
  if (candidate && !Number.isNaN(new Date(candidate).getTime())) return candidate;
  // Fallback: alguns serviços trazem a data embutida na mensagem ISO.
  const match = /\d{4}-\d{2}-\d{2}T[\d:.]+Z?/.exec(error.serverMessage);
  return match?.[0];
}

function LoginFailureBanner({ failure }: { readonly failure: LoginFailure }) {
  const Icon =
    failure.code === ErrorCode.ACCOUNT_LOCKED
      ? IconClock
      : failure.tone === 'warning'
        ? IconInfo
        : IconAlertOctagon;

  if (failure.tone === 'danger') {
    return (
      <FormError title={failure.title}>
        <p className="mt-0.5">{failure.message}</p>
      </FormError>
    );
  }

  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
    >
      <Icon size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold">{failure.title}</p>
        <p className="mt-0.5">{failure.message}</p>
        {failure.code === ErrorCode.ACCOUNT_LOCKED ? (
          <p className="mt-1.5 flex items-center gap-1 text-xs">
            <IconLock size={12} aria-hidden="true" />
            Bloqueio progressivo conforme 21 CFR §11.300(d).
          </p>
        ) : null}
      </div>
    </div>
  );
}
